import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, ShoppingBag, Package, Check, Wifi, Star, Truck, Shield, 
  CreditCard, Banknote, X, Loader2, CheckCircle, Settings
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAPTAG_LOGO = "https://customer-assets.emergentagent.com/job_726f4ba1-c975-4f39-ba02-ad3131a12031/artifacts/hgye1jst_IMG_0895%20%281%29.PNG";

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    address: '',
    phone: '',
    email: '',
    paymentMethod: 'cash'
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/shop/products`);
      if (response.data && response.data.length > 0) {
        setProducts(response.data);
      }
    } catch (error) {
      console.log('Error fetching products');
    } finally {
      setLoading(false);
    }
  };

  const handlePackageSelect = (productId, packageIndex) => {
    setSelectedPackages(prev => ({
      ...prev,
      [productId]: packageIndex
    }));
  };

  const handleBuyNow = (product) => {
    const selectedIndex = selectedPackages[product.id] || 0;
    setSelectedProduct({
      ...product,
      selectedPackage: product.packages[selectedIndex]
    });
    setShowCheckout(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.surname || !formData.address || !formData.phone || !formData.email) {
      toast.error('Plotëso të gjitha fushat');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/orders`, {
        items: [{
          product_id: selectedProduct.id,
          product_name: selectedProduct.name,
          package_label: selectedProduct.selectedPackage.label,
          quantity: selectedProduct.selectedPackage.quantity,
          price: selectedProduct.selectedPackage.price
        }],
        customer_name: formData.name,
        customer_surname: formData.surname,
        customer_address: formData.address,
        customer_phone: formData.phone,
        customer_email: formData.email,
        payment_method: formData.paymentMethod,
        total_amount: selectedProduct.selectedPackage.price
      });
      
      setOrderSuccess(true);
      toast.success('Porosia u dërgua me sukses!');
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Gabim në dërgimin e porosisë');
    } finally {
      setSubmitting(false);
    }
  };

  const resetCheckout = () => {
    setShowCheckout(false);
    setSelectedProduct(null);
    setOrderSuccess(false);
    setFormData({
      name: '',
      surname: '',
      address: '',
      phone: '',
      email: '',
      paymentMethod: 'cash'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-violet-50/30 to-white">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="floating-orb w-[500px] h-[500px] bg-violet-500/10 -top-64 -right-64 animate-float" />
        <div className="floating-orb w-[400px] h-[400px] bg-cyan-500/10 bottom-0 -left-48 animate-float" style={{ animationDelay: '2s' }} />
        <div className="absolute inset-0 grid-pattern opacity-20" />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <img 
                src={TAPTAG_LOGO}
                alt="TAPTAG Logo"
                className="w-10 h-10 rounded-full object-cover shadow-lg"
              />
              <span className="text-xl font-bold text-slate-900">TapTag</span>
            </Link>
            
            <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-2" data-testid="nav-back">
              <ArrowLeft className="w-4 h-4" />
              Kthehu
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <section className="pt-28 pb-16 relative">
        <div className="container-app text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <ShoppingBag className="w-4 h-4" />
            Dyqan Online
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-slate-900 mb-4 tracking-tight" data-testid="shop-title">
            TAPTAG <span className="bg-gradient-to-r from-violet-600 to-cyan-500 bg-clip-text text-transparent">SHOP</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Blini stikerët dhe produktet NFC për të mbrojtur objektet tuaja të vlefshme
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-12 relative z-10">
        <div className="container-app">
          <div className="flex items-center justify-center gap-3 mb-12">
            <div className="h-px bg-gradient-to-r from-transparent via-violet-300 to-transparent flex-1 max-w-32" />
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 uppercase tracking-widest">
              Produktet Tona
            </h2>
            <div className="h-px bg-gradient-to-r from-transparent via-violet-300 to-transparent flex-1 max-w-32" />
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {products.map((product) => {
              const selectedIndex = selectedPackages[product.id] || 0;
              const selectedPkg = product.packages[selectedIndex];
              
              return (
                <Card 
                  key={product.id} 
                  className="overflow-hidden border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 bg-white group"
                  data-testid={`product-card-${product.id}`}
                >
                  {/* Product Image */}
                  <div className="relative h-72 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">
                      <Wifi className="w-4 h-4 inline mr-2" />
                      NFC
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-2xl font-bold text-white drop-shadow-lg">{product.name}</h3>
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    {/* Rating */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center gap-0.5">
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                      <span className="text-sm text-slate-500">(4.9) • 100+ shitje</span>
                    </div>
                    
                    {/* Description */}
                    <p className="text-slate-600 mb-4">{product.description}</p>
                    
                    {/* Dimensions */}
                    <div className="text-sm text-slate-500 mb-4 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      <span>Përmasat: {product.dimensions}</span>
                    </div>
                    
                    {/* Features */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {product.features.map((feature, idx) => (
                        <span key={idx} className="text-sm bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full font-medium">
                          <Check className="w-3 h-3 inline mr-1" />
                          {feature}
                        </span>
                      ))}
                    </div>
                    
                    {/* Package Selection */}
                    <div className="mb-6">
                      <p className="text-sm font-semibold text-slate-700 mb-3">Zgjidh paketën:</p>
                      <div className="grid grid-cols-2 gap-3">
                        {product.packages.map((pkg, idx) => (
                          <button
                            key={idx}
                            onClick={() => handlePackageSelect(product.id, idx)}
                            className={`p-4 rounded-xl border-2 transition-all text-center ${
                              selectedIndex === idx 
                                ? 'border-violet-500 bg-violet-50 shadow-lg shadow-violet-500/20' 
                                : 'border-slate-200 hover:border-violet-300 bg-white'
                            }`}
                            data-testid={`package-btn-${product.id}-${idx}`}
                          >
                            <div className={`font-semibold text-sm ${selectedIndex === idx ? 'text-violet-700' : 'text-slate-600'}`}>
                              {pkg.label}
                            </div>
                            <div className={`text-xl font-black mt-1 ${selectedIndex === idx ? 'text-violet-600' : 'text-slate-900'}`}>
                              {pkg.price} LEK
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Buy Button */}
                    <Button
                      onClick={() => handleBuyNow(product)}
                      className="w-full h-14 bg-gradient-to-r from-violet-600 via-purple-600 to-violet-600 hover:from-violet-700 hover:via-purple-700 hover:to-violet-700 text-white rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:shadow-violet-500/30 transition-all transform hover:-translate-y-0.5"
                      data-testid={`buy-btn-${product.id}`}
                    >
                      <ShoppingBag className="w-5 h-5 mr-2" />
                      BLI TANI - {selectedPkg.price} LEK
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-fade-in-up">
            {orderSuccess ? (
              /* Success State */
              <div className="p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Porosia u Dërgua!</h2>
                <p className="text-slate-600 mb-6">
                  Faleminderit për porosinë tuaj. Do t'ju kontaktojmë së shpejti për konfirmim.
                </p>
                <Button onClick={resetCheckout} className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl px-8">
                  Mbyll
                </Button>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Përfundo Porosinë</h2>
                    <p className="text-sm text-slate-500">{selectedProduct?.name} - {selectedProduct?.selectedPackage?.label}</p>
                  </div>
                  <button onClick={resetCheckout} className="p-2 hover:bg-slate-100 rounded-full">
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmitOrder} className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700">Emri *</Label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Emri"
                        className="mt-1"
                        data-testid="checkout-name"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">Mbiemri *</Label>
                      <Input
                        name="surname"
                        value={formData.surname}
                        onChange={handleInputChange}
                        placeholder="Mbiemri"
                        className="mt-1"
                        data-testid="checkout-surname"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-700">Adresa e Plotë *</Label>
                    <Input
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Rruga, numri, qyteti"
                      className="mt-1"
                      data-testid="checkout-address"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700">Telefoni *</Label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+383 4X XXX XXX"
                        className="mt-1"
                        data-testid="checkout-phone"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">Email *</Label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="email@example.com"
                        className="mt-1"
                        data-testid="checkout-email"
                      />
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <Label className="text-slate-700 mb-3 block">Mënyra e Pagesës *</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, paymentMethod: 'cash'})}
                        className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                          formData.paymentMethod === 'cash'
                            ? 'border-violet-500 bg-violet-50'
                            : 'border-slate-200 hover:border-violet-300'
                        }`}
                        data-testid="payment-cash"
                      >
                        <Banknote className={`w-6 h-6 ${formData.paymentMethod === 'cash' ? 'text-violet-600' : 'text-slate-400'}`} />
                        <div className="text-left">
                          <p className={`font-semibold ${formData.paymentMethod === 'cash' ? 'text-violet-700' : 'text-slate-700'}`}>Cash</p>
                          <p className="text-xs text-slate-500">Paguaj në dorëzim</p>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, paymentMethod: 'card'})}
                        className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                          formData.paymentMethod === 'card'
                            ? 'border-violet-500 bg-violet-50'
                            : 'border-slate-200 hover:border-violet-300'
                        }`}
                        data-testid="payment-card"
                      >
                        <CreditCard className={`w-6 h-6 ${formData.paymentMethod === 'card' ? 'text-violet-600' : 'text-slate-400'}`} />
                        <div className="text-left">
                          <p className={`font-semibold ${formData.paymentMethod === 'card' ? 'text-violet-700' : 'text-slate-700'}`}>Kartë</p>
                          <p className="text-xs text-slate-500">Së shpejti</p>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
                    <span className="text-slate-600">Totali:</span>
                    <span className="text-2xl font-bold text-violet-600">{selectedProduct?.selectedPackage?.price} LEK</span>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-semibold"
                    data-testid="submit-order-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Duke dërguar...
                      </>
                    ) : (
                      'Dërgo Porosinë'
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 bg-slate-900 border-t border-slate-800 relative z-10">
        <div className="container-app">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img 
                src={TAPTAG_LOGO}
                alt="TAPTAG Logo"
                className="w-8 h-8 rounded-full object-cover"
              />
              <span className="text-sm text-slate-400">TAPTAG © 2024</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors">
                Faqja Kryesore
              </Link>
              <a href="mailto:support@taptag.al" className="text-sm text-slate-400 hover:text-white transition-colors">
                Kontakt
              </a>
              <Link to="/admin" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1" data-testid="admin-link">
                <Settings className="w-4 h-4" />
                Admin Panel
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ShopPage;
